(function () {
  'use strict';

  // 画面下に貼り付いている他の固定要素（アプリ埋め込みのフローティングバー、Cookie 同意バー等）を検出し、
  // その高さを --floating-cta-offset にセットして、フローティングCTAをそれらの「上」に積み上げる。
  //
  // 検出方法（自動検出時）:
  //   1. ビューポート下端付近の数点で document.elementsFromPoint() を呼び、そこに実際に描画されている要素を集める。
  //      アプリ埋め込みは DOM の階層・Shadow DOM・iframe など構造がまちまちなため、DOM を辿るのではなく
  //      「画面下端に何が見えているか」から探す。
  //   2. 念のため body 直下から数階層も走査し、候補に加える。
  // 候補のうち、以下をすべて満たす要素を「避ける対象」とみなす:
  //   - 自身または祖先が position: fixed / sticky（＝スクロールしても画面に貼り付いている）
  //   - 表示中（display / visibility / opacity）
  //   - 下端がビューポート下端に接しているか、その近く（40px 以内）にある
  //   - 幅がビューポートの半分以上（右下の丸いランチャー等、幅の狭い要素は対象外）
  //   - 高さがビューポートの半分以下（全画面モーダルや透明な全面ラッパは対象外）
  // 自動検出で拾えない場合は、セクション設定（data-offset-selector）でセレクタを明示できる。

  var BAR_SELECTOR = '[data-floating-cta-bar]';
  var MIN_WIDTH_RATIO = 0.5;
  var MAX_HEIGHT_RATIO = 0.5;
  var BOTTOM_TOLERANCE = 40; // 下端から少し浮かせて表示するバー（角丸＋余白付き等）も拾う
  var MAX_DEPTH = 4;
  var SAMPLE_X_RATIOS = [0.25, 0.5, 0.75];
  var SAMPLE_Y_OFFSETS = [6, 24];

  function isPinned(element) {
    // 自身または祖先のいずれかが fixed / sticky なら画面に貼り付いている要素とみなす
    var node = element;
    while (node && node !== document.body && node.nodeType === 1) {
      var position = window.getComputedStyle(node).position;
      if (position === 'fixed' || position === 'sticky') return true;
      node = node.parentElement || (node.getRootNode && node.getRootNode().host) || null;
    }
    return false;
  }

  function isBarAtBottom(element, viewportWidth, viewportHeight) {
    var style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (parseFloat(style.opacity) === 0) return false;

    var rect = element.getBoundingClientRect();
    if (rect.height <= 0 || rect.width <= 0) return false;
    if (rect.bottom < viewportHeight - BOTTOM_TOLERANCE) return false;
    if (rect.width < viewportWidth * MIN_WIDTH_RATIO) return false;
    if (rect.height > viewportHeight * MAX_HEIGHT_RATIO) return false;
    return isPinned(element);
  }

  function isExcluded(element, bar) {
    if (element === bar || bar.contains(element) || element.contains(bar)) return true;
    if (element === document.body || element === document.documentElement) return true;
    var tag = element.tagName;
    return tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'TEMPLATE';
  }

  function collectCandidates(bar, customSelector) {
    var candidates = [];

    function add(element) {
      if (!element || element.nodeType !== 1) return;
      if (isExcluded(element, bar)) return;
      if (candidates.indexOf(element) === -1) candidates.push(element);
    }

    if (customSelector) {
      try {
        var custom = document.querySelectorAll(customSelector);
        for (var c = 0; c < custom.length; c++) add(custom[c]);
      } catch (error) {
        // 不正なセレクタは無視して自動検出にフォールバック
      }
      if (candidates.length) return candidates;
    }

    // 1. 画面下端付近に実際に描画されている要素
    if (typeof document.elementsFromPoint === 'function') {
      var viewportWidth = window.innerWidth;
      var viewportHeight = window.innerHeight;
      for (var yi = 0; yi < SAMPLE_Y_OFFSETS.length; yi++) {
        var y = viewportHeight - SAMPLE_Y_OFFSETS[yi];
        if (y < 0) continue;
        for (var xi = 0; xi < SAMPLE_X_RATIOS.length; xi++) {
          var x = Math.round(viewportWidth * SAMPLE_X_RATIOS[xi]);
          var stack = document.elementsFromPoint(x, y);
          for (var s = 0; s < stack.length; s++) {
            add(stack[s]);
            // Shadow DOM 内に描画されている場合、document 側からはホスト要素しか返らないため中も見る
            var shadow = stack[s].shadowRoot;
            if (shadow && typeof shadow.elementsFromPoint === 'function') {
              var inner = shadow.elementsFromPoint(x, y);
              for (var n = 0; n < inner.length; n++) add(inner[n]);
            }
          }
        }
      }
    }

    // 2. body 直下から MAX_DEPTH 階層。セクション自身を含むラッパ（main 等）は対象外
    function walk(element, depth) {
      var children = element.children;
      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        if (child.contains(bar)) continue;
        add(child);
        if (depth < MAX_DEPTH) walk(child, depth + 1);
      }
    }
    walk(document.body, 1);

    return candidates;
  }

  function initBar(bar) {
    if (bar.dataset.floatingCtaInitialized === 'true') return;
    bar.dataset.floatingCtaInitialized = 'true';

    var customSelector = (bar.getAttribute('data-offset-selector') || '').trim();
    var observedTargets = [];
    var resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleMeasure) : null;
    var attributeObserver = typeof MutationObserver === 'function' ? new MutationObserver(scheduleMeasure) : null;
    var frame = null;

    function observe(targets) {
      var changed = targets.length !== observedTargets.length;
      if (!changed) {
        for (var i = 0; i < targets.length; i++) {
          if (targets[i] !== observedTargets[i]) {
            changed = true;
            break;
          }
        }
      }
      if (!changed) return;

      if (resizeObserver) resizeObserver.disconnect();
      if (attributeObserver) attributeObserver.disconnect();
      observedTargets = targets;
      for (var k = 0; k < targets.length; k++) {
        if (resizeObserver) resizeObserver.observe(targets[k]);
        if (attributeObserver) {
          attributeObserver.observe(targets[k], { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
        }
      }
    }

    function measure() {
      frame = null;
      var viewportWidth = window.innerWidth;
      var viewportHeight = window.innerHeight;
      var candidates = collectCandidates(bar, customSelector);
      var matched = [];
      var offset = 0;

      for (var i = 0; i < candidates.length; i++) {
        var element = candidates[i];
        if (!isBarAtBottom(element, viewportWidth, viewportHeight)) continue;
        matched.push(element);
        var top = element.getBoundingClientRect().top;
        offset = Math.max(offset, viewportHeight - top);
      }

      bar.style.setProperty('--floating-cta-offset', Math.round(offset) + 'px');
      observe(matched);
    }

    function scheduleMeasure() {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(measure);
    }

    measure();

    window.addEventListener('resize', scheduleMeasure);
    window.addEventListener('scroll', scheduleMeasure, { passive: true });
    window.addEventListener('load', scheduleMeasure);

    // アプリ埋め込みは非同期に差し込まれ、表示・非表示もアプリ側のタイミングで切り替わる。
    // body 直下の追加・削除の監視に加え、検出前の要素の変化は監視できないため低頻度で定期的に再計測する
    // （計測は画面下端数点の elementsFromPoint と少数の getComputedStyle だけなので負荷は小さい）。
    if (typeof MutationObserver === 'function') {
      new MutationObserver(scheduleMeasure).observe(document.body, { childList: true });
    }
    window.setInterval(scheduleMeasure, 1000);
  }

  function initAll() {
    var bars = document.querySelectorAll(BAR_SELECTOR);
    for (var i = 0; i < bars.length; i++) initBar(bars[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // テーマエディタでの再描画対応
  document.addEventListener('shopify:section:load', function (event) {
    var bar = event.target.querySelector(BAR_SELECTOR);
    if (bar) initBar(bar);
  });
})();
